import { useState } from "react";
import { Stethoscope, Building2, Handshake } from "lucide-react";
import { PageHero, SectionHead } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { useSubmit } from "../lib/api";
import { useTitle } from "../lib/useTitle";

const STEPS = [
  { icon: Stethoscope, title: "You identify a woman who could flourish here", text: "Therapists, social workers, counselors, case managers, and ministry leaders are our trusted front doors." },
  { icon: Handshake, title: "We walk in together", text: "With consent, we coordinate care so therapy, community, nutrition, and training reinforce one another." },
  { icon: Building2, title: "She grows within a full support system", text: "You remain her clinical anchor while we provide faith, food, garden, skills, and sisterhood." },
];

export default function Referrals() {
  useTitle("Professional Referrals | Bee Courageous @ Garden Grub");
  const { loading, submit } = useSubmit("/submissions");
  const [form, setForm] = useState({ refName: "", org: "", role: "", email: "", phone: "", clientName: "", clientContact: "", notes: "", consent: false });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const res = await submit({
      type: "referral",
      name: form.refName,
      email: form.email,
      phone: form.phone || undefined,
      details: {
        organization: form.org,
        role: form.role,
        client_name: form.clientName,
        client_contact: form.clientContact,
        notes: form.notes,
        client_consent: form.consent,
      },
    });
    if (res?.success) setForm({ refName: "", org: "", role: "", email: "", phone: "", clientName: "", clientContact: "", notes: "", consent: false });
  };

  return (
    <>
      <PageHero
        eyebrow="Professional Referrals"
        lines={["It takes a village.", "You are part of ours."]}
        lede="We collaborate with licensed mental health professionals, social workers, and community organizations so every woman receives coordinated, dignified care."
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <SectionHead eyebrow="How Collaboration Works" title="Three steps, one circle of care" />
        <div className="mt-12 grid gap-6 md:grid-cols-3 lg:gap-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={0.08 * i}>
              <article className="card-soft h-full" data-testid={`referral-step-${i}`}>
                <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-wing-soft text-wing">
                  <s.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="font-serif text-xl font-semibold text-charcoal">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{s.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-wing-soft/50 py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <SectionHead center eyebrow="Referral Form" title="Refer a woman to the program" text="Confidential and reviewed within two business days. Please only share client details with her explicit consent." />
          <Reveal className="mt-10">
            <form onSubmit={onSubmit} className="card-soft !border-wing/20" data-testid="referral-form">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-wing">About you (the referring professional)</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="ref-name" className="label-field">Your name</label>
                  <input id="ref-name" required className="input-field" value={form.refName} onChange={set("refName")} data-testid="referral-name-input" />
                </div>
                <div>
                  <label htmlFor="ref-org" className="label-field">Organization / practice</label>
                  <input id="ref-org" className="input-field" value={form.org} onChange={set("org")} data-testid="referral-org-input" />
                </div>
                <div>
                  <label htmlFor="ref-role" className="label-field">Your role</label>
                  <select id="ref-role" className="input-field" value={form.role} onChange={set("role")} data-testid="referral-role-select">
                    <option value="">Select…</option>
                    <option>Therapist / Counselor</option>
                    <option>Social Worker / Case Manager</option>
                    <option>Medical Professional</option>
                    <option>Pastor / Ministry Leader</option>
                    <option>Other Community Professional</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="ref-email" className="label-field">Your email</label>
                  <input id="ref-email" type="email" required className="input-field" value={form.email} onChange={set("email")} data-testid="referral-email-input" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="ref-phone" className="label-field">Your phone (optional)</label>
                  <input id="ref-phone" className="input-field" value={form.phone} onChange={set("phone")} data-testid="referral-phone-input" />
                </div>
              </div>

              <h3 className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-garden">About the woman you are referring</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="ref-client-name" className="label-field">Her name</label>
                  <input id="ref-client-name" required className="input-field" value={form.clientName} onChange={set("clientName")} data-testid="referral-client-name-input" />
                </div>
                <div>
                  <label htmlFor="ref-client-contact" className="label-field">Her preferred contact</label>
                  <input id="ref-client-contact" required className="input-field" value={form.clientContact} onChange={set("clientContact")} data-testid="referral-client-contact-input" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="ref-notes" className="label-field">How can we best support her? (optional)</label>
                  <textarea id="ref-notes" rows={4} className="input-field" value={form.notes} onChange={set("notes")} data-testid="referral-notes-input" />
                </div>
              </div>

              <label className="mt-5 flex items-start gap-3 text-sm text-charcoal/75">
                <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-wing" data-testid="referral-consent-checkbox" />
                She has given consent to be contacted by Bee Courageous @ Garden Grub.
              </label>
              <button type="submit" disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="referral-submit-btn">
                {loading ? "Sending…" : "Submit Referral"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
